import type { FileType } from "@/api/documents/documents.types";

export type DirectDocumentBudgetStatus =
    | "new-thread"
    | "pending"
    | "error"
    | "ready";

export interface DirectDocumentBudgetEvaluation {
    /** Documents already drawing on the budget. Re-selecting these is free. */
    heldCount: number;
    /** Documents this action would newly draw from the budget. */
    proposedCount: number;
    /** What the count becomes if this action goes through. */
    resultingCount: number;
    limit: number;
    /** Slots left before the cap, floored at zero. */
    remaining: number;
    wouldExceed: boolean;
    /** Convenience inverse of `wouldExceed`, for gating a confirm action. */
    admissible: boolean;
    /**
     * True when the conversation already sits above the cap. Only reachable
     * for conversations that predate the limit; everything attached stays
     * usable, but nothing new can be added.
     */
    isLegacyOverCap: boolean;
}

export interface EvaluateSelectionInput {
    /** Complete eligible set from the server, not a page of it. */
    eligibleIds: readonly number[];
    /** Composer-staged documents not yet persisted. */
    stagedIds: readonly number[];
    /** Ids the user is proposing to attach. */
    selectedIds: readonly number[];
    limit: number;
}

/**
 * Mirror of the server's candidate predicate: a document draws on the budget
 * only when its type is pdf or unknown. Spreadsheets stay attachable at any
 * count, so they must never be counted here either.
 */
export function countsAgainstDirectDocumentBudget(
    fileType: FileType | null | undefined
): boolean {
    return fileType == null || fileType === "pdf";
}

export function partitionSelectionByBudget<
    T extends { id: number; file_type?: FileType | null },
>(items: readonly T[]): { counted: number[]; exempt: number[] } {
    const counted: number[] = [];
    const exempt: number[] = [];
    for (const item of items) {
        if (countsAgainstDirectDocumentBudget(item.file_type)) {
            counted.push(item.id);
        } else {
            exempt.push(item.id);
        }
    }
    return { counted, exempt };
}

function summarize(
    heldCount: number,
    proposedCount: number,
    limit: number
): DirectDocumentBudgetEvaluation {
    const resultingCount = heldCount + proposedCount;
    const wouldExceed = proposedCount > 0 && resultingCount > limit;
    return {
        heldCount,
        proposedCount,
        resultingCount,
        limit,
        remaining: Math.max(limit - heldCount, 0),
        // An action that adds nothing is never a block. Without the proposal
        // guard a legacy conversation already above the cap would refuse to
        // re-select documents it already holds, which costs nothing.
        wouldExceed,
        admissible: !wouldExceed,
        isLegacyOverCap: heldCount > limit,
    };
}

/**
 * Dedup-aware path, for pickers that yield real document ids.
 *
 * Re-selecting something already held is free, so only ids outside
 * `eligible ∪ staged` count as proposed. This is the one path where free
 * re-selection matters: a user browsing the library can easily re-click a
 * document that is already attached.
 */
export function evaluateSelectedDocuments({
    eligibleIds,
    stagedIds,
    selectedIds,
    limit,
}: EvaluateSelectionInput): DirectDocumentBudgetEvaluation {
    const held = new Set<number>([...eligibleIds, ...stagedIds]);
    const proposed = new Set<number>();
    for (const id of selectedIds) {
        if (!held.has(id)) {
            proposed.add(id);
        }
    }
    return summarize(held.size, proposed.size, limit);
}

/**
 * Count-based path, for the upload modal.
 *
 * An uploaded PDF has no id until `confirmLibraryUpload` runs, so there is
 * nothing to dedup against and every one of them is structurally new. Routing
 * uploads through the dedup-aware path would wave through over-limit uploads
 * whose ids happen to collide with nothing.
 */
export function evaluateNewCountedDocuments(
    newCount: number,
    heldCount: number,
    limit: number
): DirectDocumentBudgetEvaluation {
    return summarize(heldCount, Math.max(newCount, 0), limit);
}

const EXEMPT_UPLOAD_EXTENSIONS = [".xlsx", ".csv"];
const EXEMPT_UPLOAD_MIME_TYPES = [
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "text/csv",
];

/**
 * Whether a file being uploaded will draw on the budget once it exists.
 *
 * The upload path has no document row yet, so the decision has to come off the
 * file itself. Anything not recognisably a spreadsheet counts, which keeps an
 * unknown type on the conservative side of the cap.
 */
export function countsUploadedFileAgainstBudget(file: {
    name: string;
    type?: string;
}): boolean {
    const name = file.name.toLowerCase();
    if (EXEMPT_UPLOAD_EXTENSIONS.some((suffix) => name.endsWith(suffix))) {
        return false;
    }
    return !EXEMPT_UPLOAD_MIME_TYPES.includes(file.type ?? "");
}

export interface DirectDocumentBudgetRejection {
    limit: number;
}

type RejectionListener = (rejection: DirectDocumentBudgetRejection) => void;

const rejectionListeners = new Set<RejectionListener>();

/**
 * Return path for a server rejection observed outside React.
 *
 * `RefineContext` deliberately scopes its equivalent signal to a provider
 * instance, but the chat model adapter that observes this rejection is not
 * rendered inside the tree and cannot read a context. Module scope is the
 * only channel available to it.
 */
export function publishDirectDocumentBudgetRejection(
    rejection: DirectDocumentBudgetRejection
): void {
    for (const listener of [...rejectionListeners]) {
        listener(rejection);
    }
}

export function subscribeDirectDocumentBudgetRejection(
    listener: RejectionListener
): () => void {
    rejectionListeners.add(listener);
    return () => {
        rejectionListeners.delete(listener);
    };
}
